import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const authHeaders = (contentType = "application/json") => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": contentType,
  Lang: i18n.language,
});

const appendParam = (queryParams, key, value) => {
  if (value === "" || value == null) return;
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    queryParams.append(key, JSON.stringify(value));
    return;
  }
  queryParams.append(key, String(value));
};

export const fetchManualGroupedOrders = createAsyncThunk(
  "manualGroupedOrders/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        appendParam(queryParams, key, value);
      });
      const qs = queryParams.toString();
      const response = await axios.get(
        `${BASE_URL}/manual-grouped-orders${qs ? `?${qs}` : ""}`,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load manual grouped orders";
      return rejectWithValue({ message });
    }
  }
);

export const addManualGroupedOrder = createAsyncThunk(
  "manualGroupedOrders/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/manual-grouped-orders`,
        formData,
        { headers: authHeaders("multipart/form-data") }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to add manual grouped order";
      const errors = error.response?.data?.errors || null;
      return rejectWithValue({ message, errors });
    }
  }
);

export const fetchManualGroupedOrder = createAsyncThunk(
  "manualGroupedOrders/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/manual-grouped-orders/${id}`,
        { headers: authHeaders() }
      );
      const body = response.data;
      const data = body?.data ?? body;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        return {
          ...data,
          teamwork_movements:
            body?.teamwork_movements ?? data.teamwork_movements ?? [],
        };
      }
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load manual grouped order";
      return rejectWithValue({ message });
    }
  }
);

export const updateManualGroupedOrderStatus = createAsyncThunk(
  "manualGroupedOrders/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/manual-grouped-orders/${id}/update-status`,
        { status },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to update manual grouped order status";
      return rejectWithValue({ message });
    }
  }
);

const normalizePagination = (payload) => {
  if (payload?.meta) {
    return {
      current_page: payload.meta.current_page,
      last_page: payload.meta.last_page,
      per_page: payload.meta.per_page,
      total: payload.meta.total,
      from: payload.meta.from,
      to: payload.meta.to,
    };
  }
  return payload?.data?.pagination || payload?.pagination || null;
};

const ManualGroupedOrdersSlice = createSlice({
  name: "manualGroupedOrders",
  initialState: {
    orders: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
    lastCreatedId: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.lastCreatedId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManualGroupedOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManualGroupedOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        state.orders = Array.isArray(payload?.data)
          ? payload.data
          : payload?.data?.data || [];
        state.pagination = normalizePagination(payload);
      })
      .addCase(fetchManualGroupedOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load data";
      })
      .addCase(addManualGroupedOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
        state.lastCreatedId = null;
      })
      .addCase(addManualGroupedOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "Manual grouped order added successfully";
        const payloadData = action.payload?.data ?? action.payload;
        state.lastCreatedId = payloadData?.id ?? null;
      })
      .addCase(addManualGroupedOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to add";
      })
      .addCase(fetchManualGroupedOrder.pending, (state) => {
        if (!state.record) state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManualGroupedOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchManualGroupedOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load record";
      })
      .addCase(updateManualGroupedOrderStatus.pending, (state) => {
        state.error = null;
        state.success = null;
      })
      .addCase(updateManualGroupedOrderStatus.fulfilled, (state, action) => {
        state.success =
          action.payload?.message || "Status updated successfully";
        const payloadData = action.payload?.data;
        if (
          state.record &&
          payloadData &&
          typeof payloadData === "object" &&
          !Array.isArray(payloadData)
        ) {
          state.record = {
            ...state.record,
            ...payloadData,
            teamwork_movements:
              action.payload?.teamwork_movements ??
              payloadData.teamwork_movements ??
              state.record.teamwork_movements,
          };
        }
      })
      .addCase(updateManualGroupedOrderStatus.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to update status";
      });
  },
});

export const { clearState } = ManualGroupedOrdersSlice.actions;
export default ManualGroupedOrdersSlice.reducer;
